import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEOContentBlock } from "@/components/seo/SEOContentBlock";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getRelatedPosts } from "@/content/blogPosts";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CalendarDays } from "lucide-react";
import heroImage from "@/assets/gallery/clear-river.jpg";
import safetyImg from "@/assets/tubing/kid-tubing.jpg";
import flowImg from "@/assets/gallery/river-canopies.jpg";

const PUBLISH_DATE = "May 9, 2026";
const CANONICAL = "/blog/guadalupe-river-water-levels-safety";
const TITLE = "Guadalupe River Water Levels & Safety — Family Guide | Son's";
const DESCRIPTION =
  "Guadalupe River water levels and safety — what families should know before tubing near New Braunfels: flow rates, life jackets, river conditions, and tips.";

const FAQS = [
  {
    q: "Is the Guadalupe River safe for tubing?",
    a: "Yes — the calm, family-friendly stretches we float at Son's Guadalupe are safe for tubing in normal conditions. We monitor river flow and weather daily and will let you know if conditions aren't right for tubing.",
  },
  {
    q: "What is a safe Guadalupe River flow rate for tubing?",
    a: "Generally, flow rates between 100 and 400 cubic feet per second (CFS) are ideal for family tubing. Above that, the river can move fast enough that we recommend caution or a cabana day instead. Below that, floats get slow but still fun.",
  },
  {
    q: "Where do I check current Guadalupe River levels?",
    a: "USGS publishes live data for the Guadalupe River near Spring Branch and at Sattler. Our team also posts current conditions on the website and answers the phone seven days a week — easiest to just call us before your trip.",
  },
  {
    q: "Should everyone wear a life jacket on the Guadalupe?",
    a: "We strongly recommend life jackets for all children and any adult who isn't a confident swimmer. Bring your own or grab one on site.",
  },
];

const schemaJson = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://sonsguadalupe.com/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: "https://sonsguadalupe.com/blog" },
        { "@type": "ListItem", position: 3, name: "Guadalupe River Water Levels & Safety — What Families Should Know", item: `https://sonsguadalupe.com/blog/guadalupe-river-water-levels-safety` },
      ],
    },
    {
      "@type": "BlogPosting",
      headline: "Guadalupe River Water Levels & Safety — What Families Should Know",
      description: DESCRIPTION,
      image: "https://sonsguadalupe.com/og-image.png",
      author: { "@type": "Organization", name: "Son's Guadalupe" },
      datePublished: "2026-05-09",
      mainEntityOfPage: { "@type": "WebPage", "@id": `https://sonsguadalupe.com${CANONICAL}` },
      keywords:
        "guadalupe river level, guadalupe river safety, is the guadalupe river safe, guadalupe river flow, tubing safety new braunfels",
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    },
  ],
};

const BlogRiverSafety = () => {
  const related = getRelatedPosts("guadalupe-river-water-levels-safety", 3);
  return (
    <div className="min-h-screen">
      <SEOHead title={TITLE} description={DESCRIPTION} canonical={CANONICAL} schemaJson={schemaJson} />
      <Header />
      <main className="pt-24 pb-16">
        <div className="relative h-[55vh] min-h-[400px]">
          <img src={heroImage} alt="Clear, calm Guadalupe River near New Braunfels at safe flow levels" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <div className="container-narrow mx-auto">
              <div className="flex items-center gap-2 text-white/80 text-sm mb-3">
                <CalendarDays className="w-4 h-4" /> {PUBLISH_DATE}
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-white max-w-4xl leading-tight">
                Guadalupe River Water Levels & Safety — What Families Should Know
              </h1>
            </div>
          </div>
        </div>

        <article className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl px-4">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-primary hover:underline mb-8">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>

            <div className="prose prose-lg max-w-none text-foreground space-y-6">
              <p className="text-xl leading-relaxed">
                The <strong>Guadalupe River</strong> is one of the most beautiful, family-friendly rivers in Texas — and like any river, it's worth understanding before you float. This guide covers water levels, safety basics, life jackets, and how Son's Guadalupe makes sure your day on the river is as safe as it is fun.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Understanding Guadalupe River Flow (CFS)</h2>
              <p>
                River flow is measured in <strong>cubic feet per second (CFS)</strong>. For family tubing, we look for the sweet spot between roughly <strong>100 and 400 CFS</strong> — fast enough to float at a good pace, slow enough that even kids can navigate calm sections comfortably. The USGS publishes live data for the Guadalupe at multiple points; we monitor it every day.
              </p>

              <img src={flowImg} alt="Tree-lined Guadalupe River flowing through River Road near New Braunfels" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">When Flow Is Higher Than Normal</h2>
              <p>
                After heavy rain, flow can climb above 600 CFS. The river still tubes — but faster, with stronger eddies. We may recommend skipping tubing and enjoying a cabana day, swim time, and grilling out instead. Cabana rentals on the Guadalupe River are a great rainy-spell backup.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">When Flow Is Low</h2>
              <p>
                Late summer dry spells can drop flow below 100 CFS. Floats are slow and you may bottom out in a few spots, but it's still a beautiful day — and some families prefer it for kids.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Life Jackets — Our #1 Safety Recommendation</h2>
              <p>
                We strongly recommend <strong>life jackets for all children</strong> and any adult who isn't a confident swimmer. Properly fitted is the key — too loose and they ride up over a kid's chin in the water. Bring your own or grab one at our property.
              </p>

              <img src={safetyImg} alt="Child wearing a life jacket while tubing the Guadalupe River safely" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Other River Safety Tips</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Hydrate.</strong> Texas sun + alcohol + river is the fastest way to a bad afternoon. Bring water.</li>
                <li><strong>Don't dive.</strong> The Guadalupe's depth varies — never dive headfirst.</li>
                <li><strong>Stay on the calm stretches.</strong> Our team will brief you on where to float.</li>
                <li><strong>Watch the weather.</strong> Flash floods are rare but real. We monitor and will pull tubes if needed.</li>
                <li><strong>No glass.</strong> Ever.</li>
                <li><strong>Keep groups together.</strong> Especially with kids.</li>
              </ul>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">How Son's Helps Keep Families Safe</h2>
              <p>
                Our team checks river conditions daily, briefs every guest, and offers life jackets on site. Cabana rentals on the Guadalupe River give families a private home base — bathrooms, shade, picnic tables, and a place to regroup between floats. It's tubing the Guadalupe with a safety net.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">River Safety FAQs</h2>
              <div className="space-y-6">
                {FAQS.map((f) => (
                  <div key={f.q}>
                    <h3 className="font-heading text-lg font-semibold text-foreground">{f.q}</h3>
                    <p className="mt-2">{f.a}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <Link to="/contact"><Button variant="default" size="lg" className="text-lg px-10 py-6">Check Conditions</Button></Link>
                <Link to="/book-now"><Button variant="outline" size="lg" className="text-lg px-10 py-6">Book a Cabana</Button></Link>
              </div>
            </div>
          </div>
        </article>
        <RelatedReads posts={related} />

      </main>
      <SEOContentBlock
        eyebrow="Plan a Safe River Day"
        heading="Tubing the Guadalupe — Safer With a Cabana"
        paragraphs={[
          "A cabana rental on the Guadalupe River gives families a private basecamp, fast access to bathrooms, and a calm spot to regroup between floats. It's the safest, easiest way to spend a day tubing near New Braunfels.",
        ]}
        links={[
          { label: "Cabanas", to: "/cabanas" },
          { label: "Tubing", to: "/tubing" },
          { label: "FAQs", to: "/faqs" },
          { label: "Contact Us", to: "/contact" },
        ]}
      />
      <Footer />
    </div>
  );
};

export default BlogRiverSafety;
